/**
 * The mechanics every full-bleed filmstrip shares: the box it bleeds out of,
 * the arrow that steps it, which way it can still go, and the stepping itself.
 *
 * PRESENTATION-FREE and component-free, so a second strip does not have to
 * import the first one to reuse them. The portfolio strip owned all of this
 * while it was the only one; it moved here the moment the homepage grew a
 * service strip with the same gesture, because the alternative was a second
 * hand-written copy of {@link scrollStripBy}'s rounding — the one part of a
 * filmstrip that is genuinely subtle and the one part a copy would get wrong
 * silently.
 */

/**
 * The page container the strip is bleeding OUT of, as two class strings that
 * have to describe the same box.
 *
 * They travel together as one object because they are two halves of one fact and
 * a caller that got only one of them right would misalign tile 1 against the
 * heading — the alignment the whole layout is built on. Each template exports
 * its own value next to the container class it mirrors, so the pair cannot drift
 * from the container it describes.
 */
export type FilmstripContainer = {
  /** The template's own container class, for the contained header row. */
  className: string;
  /**
   * Left/right padding matching that container's content inset at EVERY width,
   * plus `scroll-pl-*` equal to the left padding.
   *
   * The RIGHT padding matches the left, as the design has it: it is what lets
   * the last tile scroll all the way to the left inset instead of stopping
   * short. The bleed is not the absence of right padding — it is the row being
   * wider than the viewport.
   *
   * `scroll-padding-left` MUST match the left padding: with
   * `scroll-snap-align: start` the first tile's snap position is its offset
   * minus the scroll padding, and the arrow stepping below assumes that lands on
   * zero.
   */
  inset: string;
};

/**
 * The prev/next arrow, on whatever surface the section is painted. Rendered
 * through `StripArrows`, which every strip and the portfolio showcase share.
 *
 * Each of them sits in a section an operator can theme, and a theme sets the
 * section's background and text colour as a pair, so NO COLOUR HERE IS NAMED.
 * At rest the ring and the chevron are `current`, the section's own text
 * colour. It used to name `base-content` for a `base` surface, which went
 * near-black on a section themed dark, while the showcase kept a
 * `neutral-content` copy for its dark band, which went near-white on a section
 * themed light.
 *
 * THE HOVER INVERTS: the circle fills with the text colour and the chevron
 * takes the section's BACKGROUND colour. `background-color` does not inherit,
 * so the background comes from `--surface`, which `base.css` records on every
 * `bg-*` a section theme can paint and `text-surface` reads back. The chevron
 * is the button's CHILD (`*:`) on purpose: recolouring the button itself would
 * change the `current` its `bg-current` fills with, and the circle would fill
 * with the surface colour too. The child fades with the fill (`*:transition-
 * colors`), or the chevron would flash surface-on-surface mid-fade.
 *
 * The focus outline is the site-wide `:focus-visible` ring, whose colour
 * follows the surface it sits on (`--focus-ring` in `base.css`).
 *
 * DIMS TO 35% AT ITS END OF THE STRIP, keyed off `aria-disabled` rather than
 * `disabled` — see {@link FilmstripScrollState} for why the arrow stays
 * focusable. The hovers are scoped to `not-aria-disabled:`: `:hover` still
 * matches an `aria-disabled` button, and an end arrow that lit up under the
 * cursor would look live while doing nothing. Tailwind's bare `transition`
 * covers the opacity as well as the colours, so the dim fades in with them.
 */
export const FILMSTRIP_ARROW_CLASS =
  'flex size-12 flex-none items-center justify-center rounded-full border border-current/20 '
  + 'transition duration-200 ease-out *:transition-colors *:duration-200 *:ease-out '
  + 'not-aria-disabled:hover:bg-current not-aria-disabled:hover:border-current '
  + 'not-aria-disabled:hover:*:text-surface '
  + 'aria-disabled:opacity-35';

/**
 * Where a strip is in its scroll, as its arrows need it.
 *
 * The two `canScroll*` flags drive each arrow's `aria-disabled`, NOT its
 * `disabled`. A native `disabled` takes the button out of the focus order the
 * moment it applies — and the moment it applies is exactly when a keyboard user
 * has just pressed that arrow to reach the end, so focus would drop to `<body>`
 * mid-sequence. `aria-disabled` announces the arrow as unavailable and leaves
 * focus where it is; a press that still arrives is a no-op, because
 * {@link scrollStripBy} clamps its target into range.
 */
export type FilmstripScrollState = {
  /** The strip has anywhere to scroll at all, so the arrows are worth rendering. */
  hasOverflow: boolean;
  /** There is more of the strip behind the current position. */
  canScrollPrev: boolean;
  /** There is more of the strip ahead of the current position. */
  canScrollNext: boolean;
};

/**
 * How close to an end counts as AT it.
 *
 * `scrollLeft` is fractional on a scaled display, and a strip resting on its
 * last snap point can report a fraction of a pixel short of its maximum; an
 * exact comparison would leave the next arrow live at the end.
 *
 * The overflow has to clear the slack at BOTH ends to count, so an arrow pair
 * is only ever rendered when at least one of them does something wherever the
 * strip rests: any smaller and there is a position within a pixel of both ends
 * at once, where both arrows would be dead. A rounding overflow of a pixel or
 * two shows no arrows at all.
 */
const EDGE_TOLERANCE_PX = 1;

/**
 * Reads a strip's {@link FilmstripScrollState} off its three scroll metrics.
 *
 * Takes the metrics rather than the element so the edge arithmetic is tested
 * on plain numbers; an element satisfies the shape as it is.
 */
export const scrollStateOf = (
  { scrollLeft, scrollWidth, clientWidth }: Pick<HTMLElement, 'scrollLeft' | 'scrollWidth' | 'clientWidth'>,
): FilmstripScrollState => {
  const max = scrollWidth - clientWidth;

  return {
    hasOverflow: max > 2 * EDGE_TOLERANCE_PX,
    canScrollPrev: scrollLeft > EDGE_TOLERANCE_PX,
    canScrollNext: scrollLeft < max - EDGE_TOLERANCE_PX,
  };
};

/**
 * The distance between two snap points: one tile plus one gap.
 *
 * MEASURED, not a design constant. A prototype can hardcode the pitch because it
 * only ever renders at one width; here a tile is a percentage of the viewport
 * below `lg`, so a constant would be wrong at every width but one — and an arrow
 * target that is not a snap point makes the snap engine resolve the scroll to
 * whichever point it likes, which reads as dead and reversed clicks. Two
 * adjacent tiles' offsets give the true pitch at whatever width is live,
 * including the gap, with no constant to keep in sync.
 *
 * Falls back to the scrollport width for a strip with fewer than two tiles, or
 * one whose tiles have not been laid out yet. Nothing can scroll in either
 * case, so the value only has to be non-zero — it is the divisor below.
 */
const pitchOf = (el: HTMLElement): number => {
  const [first, second] = [el.children[0], el.children[1]];

  if (!(first instanceof HTMLElement) || !(second instanceof HTMLElement)) {
    return el.clientWidth;
  }

  const pitch = second.offsetLeft - first.offsetLeft;

  return pitch > 0 ? pitch : el.clientWidth;
};

/**
 * Scrolls a strip exactly one snap point, in `dir`.
 *
 * Rounding the CURRENT position to a snap index before stepping — rather than
 * adding a pitch to `scrollLeft` — is what makes a click after a free scroll
 * land on a snap point rather than one pitch away from wherever the finger
 * stopped. The target is then clamped into range, so a click at either end is a
 * no-op instead of a scroll the engine silently rejects.
 *
 * TAKES THE NULLABLE REF VALUE, rather than leaving the caller to guard. A ref
 * is null only before the first commit — which is before the buttons that call
 * this exist to be clicked — so a guard inside the component would be a branch
 * the component's own tests can never reach. Here it is an ordinary argument,
 * covered by calling this with `null`.
 */
export const scrollStripBy = (el: HTMLElement | null, dir: 1 | -1): void => {
  if (el === null) { return; }

  const pitch = pitchOf(el);
  const target = (Math.round(el.scrollLeft / pitch) + dir) * pitch;
  const max = el.scrollWidth - el.clientWidth;

  el.scrollTo({ left: Math.max(0, Math.min(max, target)), behavior: 'smooth' });
};
